import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trash2, Settings2, Sparkles, CalendarClock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';
import { AddLineButton } from '../../../shared/components/common';
import { taxDeterminationApi } from '../services/friendlyApi';
import type { TaxDeterminationResult } from '../types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '../../../components/ui/table';
import { ProductReferencePicker } from '../../master_data/products/components/ProductReferencePicker';
import { productReferenceFromFields } from '../../master_data/products/types';
import type { Product, ProductReference, ResolvedProductReference } from '../../master_data/products/types';
import axiosInstance from '../../../shared/api/axiosInstance';
import type { Account, AccountType, TaxRate, Dimension, LineDimensionAssignment } from '../types';
import { LineDimensionsCell } from './LineDimensionsCell';

export interface PendingLineItem {
  id: string;
  product_id: string;
  /** OFF-5: offerings supertype handle from the offering-aware picker; backend re-derives canonically. */
  offering_id: string | null;
  packaging_id: string | null;
  description: string;
  account_id: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  tax_rate_id: string;
  tax_amount: number;
  line_total: number;
  /** Inclusive-tax ownership marker carried from a loaded line so re-saving a
   *  generated inclusive invoice does not re-extract the VAT (WI fc8f6615).
   *  Undefined on new lines → backend uses the tax rate's inclusive default. */
  prices_include_tax?: boolean;
  /** Analytic dimension assignments (bill lines only). Optional so existing
   *  PendingLineItem construction sites (invoices, quick-create) compile unchanged. */
  dimensions?: LineDimensionAssignment[];
  /** Deferred-revenue service period (invoices only, gap [33]). Both set + a
   *  configured deferred-revenue account defers this line's revenue. */
  service_period_start?: string | null;
  service_period_end?: string | null;
}

/**
 * Document-line facts the tax determination engine resolves a rate against.
 * When provided, lines with a product and no explicit tax pick auto-suggest a
 * rate via the resolve endpoint (jurisdiction-based). See
 * docs/modules/accounting/tax-determination-design.md.
 */
export interface TaxResolveContext {
  companyId?: string;
  customerId?: string;
  shipToCountry?: string | null;
  shipToRegion?: string | null;
}

interface InvoiceLineEditorProps {
  lines: PendingLineItem[];
  onChange: (lines: PendingLineItem[]) => void;
  accounts: Account[];
  taxRates: TaxRate[];
  products?: Product[];
  /** Filter accounts to specific types (e.g. ['revenue'] for invoices, ['expense'] for expenses) */
  accountTypeFilter?: AccountType[];
  /** Default account_id applied to lines that don't have one set */
  defaultAccountId?: string;
  /** Whether to start in advanced mode (showing per-line account column) */
  initialAdvanced?: boolean;
  /**
   * Force the per-line account column open regardless of the internal toggle.
   * Bills drive this from validation (a line missing its expense account) so the
   * user can pick the account instead of the line being silently dropped.
   */
  forceAdvanced?: boolean;
  /**
   * Allow removing the last remaining line (draft goes to zero lines). Off by
   * default so the remove button is disabled on the last line — bills need ≥1
   * line and their handleLinesChange early-returns on zero, which would make the
   * removal a silent local-only no-op (WI b14ca05d). Invoices opt in: a draft
   * invoice may legitimately hold zero lines.
   */
  allowEmpty?: boolean;
  /** Applicable analytic axes (bill lines). When non-empty, a Dimensions column shows. */
  dimensions?: Dimension[];
  /**
   * When set, auto-suggest the tax rate for lines with a product where the user
   * hasn't explicitly picked one. A manual pick always wins (sent verbatim).
   */
  taxResolveContext?: TaxResolveContext | null;
  /** Show the per-line deferred-revenue service-period control (invoices only). */
  showServicePeriod?: boolean;
  /**
   * Resolve and prefill the unit price when a product is picked (sell-side
   * documents only — invoices). Off by default so purchase-side editors (bills)
   * don't prefill a sell price. Pricing moved to offerings/price-lists, so the
   * catalog `Product` no longer carries a usable `base_price`; the price comes
   * from the product-reference resolve endpoint (same source as DealProductsEditor).
   */
  resolveUnitPriceOnPick?: boolean;
}

function calcLineTotal(qty: number, price: number, discountPct: number): number {
  const subtotal = qty * price;
  return subtotal - subtotal * (discountPct / 100);
}

function calcTaxAmount(lineTotal: number, taxRate: TaxRate | undefined, isInclusive: boolean): number {
  if (!taxRate) return 0;
  const rate = taxRate.rate; // already a decimal: 0.10 = 10%
  if (isInclusive) return lineTotal - lineTotal / (1 + rate);
  return lineTotal * rate;
}

let lineIdCounter = 0;
export function createEmptyLine(): PendingLineItem {
  lineIdCounter++;
  return {
    id: `line-${lineIdCounter}-${Date.now()}`,
    product_id: '',
    offering_id: null,
    packaging_id: null,
    description: '',
    account_id: '',
    quantity: 1,
    unit_price: 0,
    discount_percent: 0,
    tax_rate_id: '',
    tax_amount: 0,
    line_total: 0,
    dimensions: [],
    service_period_start: null,
    service_period_end: null,
  };
}

export function InvoiceLineEditor({
  lines,
  onChange,
  accounts,
  taxRates,
  products,
  accountTypeFilter,
  defaultAccountId,
  initialAdvanced = false,
  forceAdvanced = false,
  allowEmpty = false,
  dimensions,
  taxResolveContext,
  showServicePeriod = false,
  resolveUnitPriceOnPick = false,
}: InvoiceLineEditorProps) {
  const { t } = useTranslation('accounting');
  const showProducts = products && products.length > 0;
  const showDimensions = !!dimensions && dimensions.length > 0;
  const [showAdvanced, setShowAdvanced] = useState(initialAdvanced);

  // Tax determination: lines the user manually set a tax on (never auto-touched)
  // vs. lines whose tax we auto-applied from a resolved rule (re-resolved when
  // the customer/ship-to context changes). A line loaded with a persisted rate
  // is in neither set → treated as explicit, left untouched.
  const manualTaxLineIds = useRef<Set<string>>(new Set());
  const autoResolvedLineIds = useRef<Set<string>>(new Set());
  const [taxSuggestions, setTaxSuggestions] = useState<Record<string, TaxDeterminationResult>>({});
  const linesRef = useRef(lines);
  linesRef.current = lines;

  // Check if any line has a different account from the default (needs advanced view)
  const hasPerLineAccounts = useMemo(() => {
    if (!defaultAccountId) return false;
    return lines.some((l) => l.account_id && l.account_id !== defaultAccountId);
  }, [lines, defaultAccountId]);

  // Auto-enable advanced if lines have differing accounts, or when a consumer
  // forces the account column open (bill validation for an account-less line).
  const effectiveAdvanced = showAdvanced || hasPerLineAccounts || forceAdvanced;

  const leafAccounts = useMemo(
    () => accounts.filter((a) => {
      if (a.is_header || !a.is_active) return false;
      if (accountTypeFilter && accountTypeFilter.length > 0) {
        return accountTypeFilter.includes(a.account_type);
      }
      return true;
    }),
    [accounts, accountTypeFilter]
  );

  const taxRateMap = useMemo(() => {
    const map = new Map<string, TaxRate>();
    taxRates.forEach((tr) => map.set(tr.id, tr));
    return map;
  }, [taxRates]);

  const productMap = useMemo(() => {
    const map = new Map<string, Product>();
    products?.forEach((p) => map.set(p.id, p));
    return map;
  }, [products]);

  // --- Tax determination resolution ---------------------------------------
  const recomputeTax = (line: PendingLineItem): PendingLineItem => {
    const gross = calcLineTotal(line.quantity, line.unit_price, line.discount_percent);
    const taxRate = line.tax_rate_id ? taxRateMap.get(line.tax_rate_id) : undefined;
    // Honor the persisted inclusive-ownership snapshot when present so the preview
    // matches what the backend keeps (explicit-flag-wins): a loaded line whose rate
    // was later toggled inclusive/exclusive still previews from its own marker, not
    // the live rate flag. Undefined marker (new line, or a fresh rate pick that
    // cleared it) falls back to the rate's default (WI 4eb7e1c5, fc8f6615).
    const isInclusive = line.prices_include_tax ?? taxRate?.is_inclusive ?? false;
    const taxAmount = calcTaxAmount(gross, taxRate, isInclusive);
    // Inclusive tax is extracted from the entered price (gross already contains VAT),
    // so the line total holds the NET (gross - tax) and subtotal + tax == gross. An
    // exclusive line keeps the gross and adds tax on top. Mirrors the backend
    // (invoice_service._set_lines) — see docs/erp/vat-sales-tax-design.md.
    const lineTotal = isInclusive ? gross - taxAmount : gross;
    return { ...line, line_total: lineTotal, tax_amount: taxAmount };
  };

  // Apply a resolved result to a line (by id, reading latest state) unless the
  // user has since picked manually. Only overwrites the rate when it changed.
  const applyResolution = useCallback(
    (lineId: string, result: TaxDeterminationResult) => {
      if (manualTaxLineIds.current.has(lineId)) return;
      autoResolvedLineIds.current.add(lineId);
      setTaxSuggestions((prev) => ({ ...prev, [lineId]: result }));
      const current = linesRef.current;
      const idx = current.findIndex((l) => l.id === lineId);
      if (idx < 0) return;
      const newRateId = result.tax_rate_id ?? '';
      if (current[idx].tax_rate_id === newRateId) return;
      const updated = [...current];
      updated[idx] = recomputeTax({ ...current[idx], tax_rate_id: newRateId });
      onChange(updated);
    },
    // recomputeTax/taxRateMap captured via closure; onChange is stable from parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, taxRateMap],
  );

  const resolveLineTax = useCallback(
    (line: PendingLineItem) => {
      const ctx = taxResolveContext;
      // company_id is optional — the backend resolves it from the auth context
      // (single-company tenants carry a null invoice.company_id). We still need a
      // product to have a meaningful line context to resolve against.
      if (!ctx || !line.product_id) return;
      if (manualTaxLineIds.current.has(line.id)) return;
      // Eligible: empty tax, or a rate we previously auto-applied.
      if (line.tax_rate_id && !autoResolvedLineIds.current.has(line.id)) return;
      const product = productMap.get(line.product_id);
      taxDeterminationApi
        .resolve({
          company_id: ctx.companyId || undefined,
          tax_type: 'sales',
          customer_id: ctx.customerId || undefined,
          ship_to_country: ctx.shipToCountry || undefined,
          ship_to_region: ctx.shipToRegion || undefined,
          product_tax_category: product?.tax_category || undefined,
        })
        .then((r) => applyResolution(line.id, r.data))
        .catch(() => { /* preview only — leave the line untouched on failure */ });
    },
    [taxResolveContext, productMap, applyResolution],
  );

  // Re-resolve all eligible lines when the customer/ship-to context changes.
  const contextKey = taxResolveContext
    ? `${taxResolveContext.companyId ?? ''}|${taxResolveContext.customerId ?? ''}|${taxResolveContext.shipToCountry ?? ''}|${taxResolveContext.shipToRegion ?? ''}`
    : '';
  useEffect(() => {
    if (!taxResolveContext) return;
    linesRef.current.forEach((line) => resolveLineTax(line));
    // Driven by context identity only — must not depend on `lines` (would loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  const updateLine = (index: number, field: keyof PendingLineItem, value: string | number | null) => {
    const updated = [...lines];
    const line = recomputeTax({ ...updated[index], [field]: value });
    updated[index] = line;
    onChange(updated);
  };

  // User picked a tax rate manually → wins verbatim, never auto-overridden.
  const handleManualTaxChange = (index: number, value: string) => {
    const line = lines[index];
    manualTaxLineIds.current.add(line.id);
    autoResolvedLineIds.current.delete(line.id);
    setTaxSuggestions((prev) => {
      const next = { ...prev };
      delete next[line.id];
      return next;
    });
    // A fresh pick of a DIFFERENT rate drops any inclusive-ownership marker carried
    // from the loaded line — "use the newly chosen rate's inclusive default". Without
    // this the preview would resolve inclusive-ness from the new rate while the save
    // payload still shipped the stale marker, persisting math the user never saw
    // (WI 4eb7e1c5). Re-picking the SAME rate keeps the snapshot (recomputeTax honors it).
    const rateChanged = value !== line.tax_rate_id;
    const updated = [...lines];
    updated[index] = recomputeTax({
      ...updated[index],
      tax_rate_id: value,
      ...(rateChanged ? { prices_include_tax: undefined } : {}),
    });
    onChange(updated);
  };

  // Resolve the catalog/price-list unit price for a freshly picked product and
  // prefill it (invoices only). Pricing lives on offerings/price-lists now, so
  // the catalog Product carries no usable base_price — the resolve endpoint is
  // the source (mirrors DealProductsEditor). Never clobbers a price the user
  // has typed, and re-reads latest state by line id (the pick is async).
  const resolveLineUnitPrice = useCallback(
    async (ref: ProductReference, lineId: string) => {
      try {
        const res = await axiosInstance.post<{ data: ResolvedProductReference[] }>(
          '/api/v1/master-data/products/product-references/resolve',
          { references: [ref], price_list_id: null },
        );
        const price = res.data.data[0]?.unit_price;
        if (price == null) return;
        const current = linesRef.current;
        const idx = current.findIndex((l) => l.id === lineId);
        if (idx < 0 || current[idx].unit_price !== 0) return; // don't overwrite a typed price
        const updated = [...current];
        updated[idx] = recomputeTax({ ...current[idx], unit_price: price });
        onChange(updated);
      } catch {
        /* preview-only prefill — leave the price untouched on failure */
      }
    },
    // recomputeTax captured via closure; onChange is stable from parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange],
  );

  const handleProductRefChange = (index: number, ref: ProductReference | null) => {
    const updated = [...lines];
    const productId = ref?.product_id ?? '';
    const productChanged = productId !== updated[index].product_id;
    const line = {
      ...updated[index],
      product_id: productId,
      offering_id: ref?.offering_id ?? null,
      packaging_id: ref?.packaging_id ?? null,
    };

    // Auto-fill the description from the product when the product changes. Price
    // is resolved separately (resolveLineUnitPrice) because pricing moved off the
    // catalog Product onto offerings/price-lists — Product.base_price is unset.
    if (productChanged) {
      const product = productId ? productMap.get(productId) : undefined;
      if (product && !line.description) line.description = product.name;
    }

    // Recalculate totals
    const recomputed = recomputeTax(line);
    updated[index] = recomputed;
    onChange(updated);

    if (productChanged) {
      // Re-suggest the tax rate when the product changes (jurisdiction-based).
      resolveLineTax(recomputed);
      // Prefill the unit price from the catalog/price-list (sell-side only).
      if (resolveUnitPriceOnPick && ref?.product_id) resolveLineUnitPrice(ref, recomputed.id);
    }
  };

  const removeLine = (index: number) => {
    onChange(lines.filter((_, i) => i !== index));
  };

  const addLine = () => {
    onChange([...lines, createEmptyLine()]);
  };

  const subtotal = lines.reduce((sum, l) => sum + l.line_total, 0);
  const taxTotal = lines.reduce((sum, l) => sum + l.tax_amount, 0);
  const total = subtotal + taxTotal;

  // Column count for footer colSpan
  let colCount = 6; // description, qty, price, discount, tax, total
  if (showProducts) colCount++;
  if (effectiveAdvanced) colCount++;
  if (showDimensions) colCount++;

  // Sum of declared column widths so the table never compresses dense inputs below
  // their min-width — the overflow-x-auto wrapper scrolls instead (ui-list-pages §16.2).
  // Base: description 200 + qty 80 + price 100 + discount 80 + tax 130 + total 100 + action 40.
  const tableMinWidth =
    730 + (showProducts ? 180 : 0) + (effectiveAdvanced ? 160 : 0) + (showDimensions ? 150 : 0);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <Table style={{ minWidth: `${tableMinWidth}px` }}>
          <TableHeader>
            <TableRow>
              {showProducts && (
                <TableHead className="min-w-[180px]">{t('lineItems.product')}</TableHead>
              )}
              <TableHead className="min-w-[200px]">{t('lineItems.description')}</TableHead>
              {effectiveAdvanced && (
                <TableHead className="min-w-[160px]">{t('revenueSetup.revenueCategory')}</TableHead>
              )}
              <TableHead className="min-w-[80px] text-right">{t('lineItems.quantity')}</TableHead>
              <TableHead className="min-w-[100px] text-right">{t('lineItems.unitPrice')}</TableHead>
              <TableHead className="min-w-[80px] text-right">{t('lineItems.discount')}</TableHead>
              <TableHead className="min-w-[130px]">{t('lineItems.tax')}</TableHead>
              {showDimensions && (
                <TableHead className="min-w-[150px]">{t('dimensions.lineAssignment')}</TableHead>
              )}
              <TableHead className="w-[100px] text-right">{t('lineItems.lineTotal')}</TableHead>
              <TableHead className={showServicePeriod ? 'w-[80px]' : 'w-[40px]'} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line, index) => (
              <TableRow key={line.id}>
                {showProducts && (
                  <TableCell>
                    <ProductReferencePicker
                      value={productReferenceFromFields(line.product_id, line.packaging_id, line.offering_id)}
                      onChange={(ref) => handleProductRefChange(index, ref)}
                      mode="catalog-only"
                      showLabels={false}
                      popoverWidth={300}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Input
                    size="sm"
                    value={line.description}
                    onChange={(e) => updateLine(index, 'description', e.target.value)}
                    placeholder={t('lineItems.descriptionPlaceholder')}
                  />
                </TableCell>
                {effectiveAdvanced && (
                  <TableCell>
                    <Select
                      value={line.account_id || defaultAccountId || ''}
                      onValueChange={(v) => updateLine(index, 'account_id', v)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder={t('revenueSetup.selectRevenueCategory')} />
                      </SelectTrigger>
                      <SelectContent>
                        {leafAccounts.map((acc) => (
                          <SelectItem key={acc.id} value={acc.id}>
                            {acc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                )}
                <TableCell>
                  <Input
                    size="sm"
                    type="number"
                    min={0}
                    step={1}
                    value={line.quantity}
                    onChange={(e) => updateLine(index, 'quantity', Number(e.target.value))}
                    className="text-right"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    size="sm"
                    type="number"
                    min={0}
                    step={0.01}
                    value={line.unit_price}
                    onChange={(e) => updateLine(index, 'unit_price', Number(e.target.value))}
                    className="text-right"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    size="sm"
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={line.discount_percent}
                    onChange={(e) => updateLine(index, 'discount_percent', Number(e.target.value))}
                    className="text-right"
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={line.tax_rate_id}
                    onValueChange={(v) => handleManualTaxChange(index, v === 'none' ? '' : v)}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder={t('lineItems.noTax')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('lineItems.noTax')}</SelectItem>
                      {taxRates.filter((tr) => tr.is_active).map((tr) => (
                        <SelectItem key={tr.id} value={tr.id}>
                          {tr.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const suggestion = taxSuggestions[line.id];
                    if (!suggestion || (suggestion.source !== 'rule' && suggestion.source !== 'default')) {
                      return null;
                    }
                    return (
                      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <Sparkles className="h-3 w-3 shrink-0 text-primary" />
                        <span className="truncate">
                          {suggestion.source === 'rule'
                            ? t('lineItems.taxSuggestedRule', { rule: suggestion.matched_rule_name })
                            : t('lineItems.taxSuggestedDefault')}
                        </span>
                      </div>
                    );
                  })()}
                </TableCell>
                {showDimensions && (
                  <TableCell>
                    <LineDimensionsCell
                      dimensions={dimensions!}
                      value={line.dimensions ?? []}
                      onChange={(dims) => {
                        const updated = [...lines];
                        updated[index] = { ...updated[index], dimensions: dims };
                        onChange(updated);
                      }}
                    />
                  </TableCell>
                )}
                <TableCell className="text-right font-mono text-sm">
                  {formatCurrency(line.line_total)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-0.5">
                    {showServicePeriod && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'h-8 w-8',
                              line.service_period_start && line.service_period_end
                                ? 'text-primary'
                                : 'text-muted-foreground',
                            )}
                            title={t('lineItems.servicePeriod')}
                          >
                            <CalendarClock className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 space-y-3" align="end">
                          <div className="space-y-1">
                            <p className="text-sm font-medium">{t('lineItems.servicePeriod')}</p>
                            <p className="text-xs text-muted-foreground">
                              {t('lineItems.servicePeriodHint')}
                            </p>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">{t('lineItems.servicePeriodStart')}</Label>
                            <Input
                              size="sm"
                              type="date"
                              value={line.service_period_start ?? ''}
                              onChange={(e) =>
                                updateLine(index, 'service_period_start', e.target.value || null)
                              }
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">{t('lineItems.servicePeriodEnd')}</Label>
                            <Input
                              size="sm"
                              type="date"
                              min={line.service_period_start ?? undefined}
                              value={line.service_period_end ?? ''}
                              onChange={(e) =>
                                updateLine(index, 'service_period_end', e.target.value || null)
                              }
                            />
                          </div>
                          {(line.service_period_start || line.service_period_end) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-full text-muted-foreground"
                              onClick={() => {
                                updateLine(index, 'service_period_start', null);
                                updateLine(index, 'service_period_end', null);
                              }}
                            >
                              {t('common:app.clear')}
                            </Button>
                          )}
                        </PopoverContent>
                      </Popover>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => removeLine(index)}
                      disabled={!allowEmpty && lines.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={colCount + 1} className="p-2">
                <AddLineButton label={t('common:app.addLine')} onClick={addLine} />
              </TableCell>
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={colCount} className="text-right font-medium">
                {t('lineItems.subtotal')}
              </TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(subtotal)}</TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell colSpan={colCount} className="text-right font-medium">
                {t('lineItems.taxTotal')}
              </TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(taxTotal)}</TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell colSpan={colCount} className="text-right font-bold">
                {t('lineItems.total')}
              </TableCell>
              <TableCell className="text-right font-mono font-bold">{formatCurrency(total)}</TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      {/* When the account column is forced open (bill validation for an
          account-less line) the toggle would read a misleading "Show advanced"
          and couldn't actually hide the column — so suppress it entirely. The
          label otherwise tracks the effective (not just the internal) state. */}
      {!forceAdvanced && (
        <div className="flex items-center justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(!effectiveAdvanced)}
            className="text-xs text-muted-foreground"
          >
            <Settings2 className="mr-1 h-3.5 w-3.5" />
            {effectiveAdvanced ? t('revenueSetup.hideAdvanced') : t('revenueSetup.showAdvanced')}
          </Button>
        </div>
      )}
    </div>
  );
}

